import { AbstractService } from "./AbstractService";
import { ConfigurableInterface } from "./ConfigurableInterface";
import axios from "axios";
import {
  connect,
  createLocalTracks,
  Room,
  LocalVideoTrackPublication,
  LocalAudioTrackPublication
} from "twilio-video";
import _ from "lodash";

export interface TwilioServiceInterface extends ConfigurableInterface {
  getAccessToken(roomName: string): Promise<string>;
  joinRoom(
    name: string,
    token: string,
    mediaInputs?: {
      camera?: string;
      microphone?: string;
    }
  ): Promise<Partial<Room>>;
  getCameras(
    room?: Room
  ): Promise<
    {
      camera: MediaDeviceInfo;
      isActive: boolean;
    }[]
  >;
  switchCamera(room: Room, deviceId: string): Promise<HTMLMediaElement>;
  getMicrophones(
    room?: Room
  ): Promise<
    {
      microphone: MediaDeviceInfo;
      isActive: boolean;
    }[]
  >;
  switchMicrophone(room: Room, deviceId: string): Promise<any>;
  enableCamera(room: Room): void;
  disableCamera(room: Room): void;
  isCameraEnabled(room: Room): boolean;
}

export class TwilioService extends AbstractService
  implements TwilioServiceInterface {
  private videoSize = {
    width: 1280,
    height: 720
  };

  public async getAccessToken(roomName: string): Promise<string> {
    this.checkConfig(["tmsServiceUrl"]);

    return (
      await axios.post(`${this.config.tmsServiceUrl}/join-room`, { roomName })
    ).data.token;
  }

  public async joinRoom(
    name: string,
    token: string,
    mediaInputs?: {
      camera?: string;
      microphone?: string;
    }
  ): Promise<Room> {
    const localTracks = await createLocalTracks({
      audio: mediaInputs?.microphone
        ? {
            deviceId: mediaInputs.microphone
          }
        : true,
      video: _.assign(
        mediaInputs?.camera
          ? {
              deviceId: mediaInputs.camera
            }
          : {},
        this.videoSize
      )
    });

    return connect(token, { name, tracks: localTracks });
  }

  public async getCameras(
    room?: Room
  ): Promise<
    {
      camera: MediaDeviceInfo;
      isActive: boolean;
    }[]
  > {
    let cameraLabel: string;
    room?.localParticipant.videoTracks.forEach(
      (publication: LocalVideoTrackPublication) => {
        const label = publication.track.mediaStreamTrack.label;
        if (label) {
          cameraLabel = label;
        }
      }
    );

    return _.map(
      (await navigator.mediaDevices.enumerateDevices()).filter(
        device => device.kind === "videoinput"
      ),
      camera => ({
        camera,
        isActive: camera.label === cameraLabel
      })
    );
  }

  public async switchCamera(
    room: Room,
    deviceId: string
  ): Promise<HTMLMediaElement> {
    const publication = room.localParticipant?.videoTracks?.entries().next()
      .value[1];

    await publication?.track.restart(_.assign({ deviceId }, this.videoSize));

    return publication?.track.attach();
  }

  public async getMicrophones(
    room?: Room
  ): Promise<
    {
      microphone: MediaDeviceInfo;
      isActive: boolean;
    }[]
  > {
    let microphoneLabel: string;
    room?.localParticipant.audioTracks.forEach(
      (publication: LocalAudioTrackPublication) => {
        const label = publication.track.mediaStreamTrack.label;
        if (label) {
          microphoneLabel = label;
        }
      }
    );

    return _.map(
      (await navigator.mediaDevices.enumerateDevices()).filter(
        device => device.kind === "audioinput"
      ),
      microphone => ({
        microphone,
        isActive: microphone.label === microphoneLabel
      })
    );
  }

  public async switchMicrophone(room: Room, deviceId: string): Promise<any> {
    const publication = room.localParticipant?.audioTracks?.entries().next()
      .value[1];

    await publication?.track.restart({ deviceId });

    return publication?.track.attach();
  }

  public enableCamera(room: Room) {
    room?.localParticipant?.videoTracks?.forEach?.(publication => {
      publication.track.enable();
    });
  }

  public disableCamera(room: Room) {
    room?.localParticipant?.videoTracks?.forEach?.(publication => {
      publication.track.disable();
    });
  }

  public isCameraEnabled(room: Room): boolean {
    let result = false;

    room?.localParticipant?.videoTracks?.forEach(
      (publication: LocalVideoTrackPublication) => {
        if (publication.isEnabled) {
          result = true;
        }
      }
    );

    return result;
  }
}

export class StagingTwilioService extends AbstractService
  implements TwilioServiceInterface {
  public async getAccessToken(roomName: string): Promise<string> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve("fake-access-token");
      }, this.config.stagingRequestDuration);
    });
  }

  public async joinRoom(
    name: string,
    token: string,
    mediaInputs?: {
      camera?: string;
      microphone?: string;
    }
  ): Promise<Partial<Room>> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          dominantSpeaker: null,
          isRecording: false,
          mediaRegion: "",
          name: "",
          participants: new Map(),
          sid: "",
          state: ""
        });
      }, this.config.stagingRequestDuration);
    });
  }

  public async getCameras(
    room?: Room
  ): Promise<
    {
      camera: MediaDeviceInfo;
      isActive: boolean;
    }[]
  > {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve([]);
      }, this.config.stagingRequestDuration);
    });
  }

  public async switchCamera(
    room: Room,
    deviceId: string
  ): Promise<HTMLMediaElement> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(new HTMLMediaElement());
      }, this.config.stagingRequestDuration);
    });
  }

  public async getMicrophones(
    room?: Room
  ): Promise<
    {
      microphone: MediaDeviceInfo;
      isActive: boolean;
    }[]
  > {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve([]);
      }, this.config.stagingRequestDuration);
    });
  }

  public async switchMicrophone(room: Room, deviceId: string): Promise<any> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(null);
      }, this.config.stagingRequestDuration);
    });
  }
  public async disableCamera(room: Room): Promise<any> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(undefined);
      }, this.config.stagingRequestDuration);
    });
  }

  public async enableCamera(room: Room): Promise<any> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(undefined);
      }, this.config.stagingRequestDuration);
    });
  }

  public isCameraEnabled(room: Room): boolean {
    return true;
  }
}
